import { Monaco } from "@stedi/dls/MonacoEditor";
import { capitalCase } from "change-case";
import memoize from "lodash/memoize";
import type { IRange, languages } from "monaco-editor";
import { builtInFunctionNames } from "../jsonata/functionsAutocomplete";
import { getWordAtOrUntilPosition } from "../monaco/monaco";
import { sanitizePath, splitPathBySymbolUnlessWithinQuotesOrBackticks } from "../path";
import { getSchemaAutocompleteEntries, getSchemaDefinitionForDataPath } from "./autocomplete";
import { MappingsJSONSchema, MappingsJSONSchemaDefinition } from "./jsonSchema";

type ScopedJSONSchema = MappingsJSONSchema | MappingsJSONSchemaDefinition | MappingsJSONSchemaDefinition[] | undefined;

interface SetupMonacoJSONSchemaProps {
  monaco: Monaco;
  rootJSONSchema: MappingsJSONSchema;
  scopedJSONSchema: ScopedJSONSchema;
}

export const setupMonacoJSONSchema = ({ monaco, rootJSONSchema, scopedJSONSchema }: SetupMonacoJSONSchemaProps) => {
  const JSONSchemaAutocompleteProvider = setupJSONSchemaAutocomplete({
    rootJSONSchema,
    scopedJSONSchema,
    monaco,
  });

  const cleanup = () => {
    JSONSchemaAutocompleteProvider.dispose();
  };

  return cleanup;
};

interface SetupJSONSchemaAutocompleteProps {
  monaco: Monaco;
  rootJSONSchema: MappingsJSONSchema;
  scopedJSONSchema: ScopedJSONSchema;
}

const setupJSONSchemaAutocomplete = ({
  monaco,
  rootJSONSchema,
  scopedJSONSchema,
}: SetupJSONSchemaAutocompleteProps) => {
  const jsonataAutocompleteProvider = monaco.languages.registerCompletionItemProvider("jsonata", {
    triggerCharacters: [".", "["],
    provideCompletionItems: (model, position) => {
      const currentWord = getWordAtOrUntilPosition(model, position);

      const range: IRange = {
        startLineNumber: position.lineNumber,
        endLineNumber: position.lineNumber,
        startColumn: currentWord.startColumn,
        endColumn: currentWord.endColumn,
      };

      const currentValue = model.getValueInRange({
        startLineNumber: position.lineNumber,
        startColumn: 0,
        endLineNumber: position.lineNumber,
        endColumn: currentWord.endColumn,
      });

      const expressions = currentValue.split(" ");
      const lastExpression = expressions[expressions.length - 1];

      const generatedSuggestions = generateAutocompleteSuggestions({
        currentWord: currentWord.word,
        currentlyTyping: removeJSONataReferences(lastExpression),
        schema: getAutocompleteJSONSchema({
          lastExpression,
          rootJSONSchema,
          scopedJSONSchema,
        }),
        range,
      });

      // If there are multiple suggestions - we don't want Monaco to cache them, so we mark the results as "incomplete".
      // It allows us to change the sorting order of suggestions based on the user input.
      const shouldNotCacheSuggestions = generatedSuggestions.length > 1;
      return {
        suggestions: generatedSuggestions,
        incomplete: shouldNotCacheSuggestions,
      };
    },
  });

  return jsonataAutocompleteProvider;
};

interface GenerateAutocompleteSuggestionsProps {
  schema: ScopedJSONSchema;
  currentlyTyping: string;
  currentWord: string;
  range: IRange;
}

export const generateAutocompleteSuggestions = ({
  currentWord,
  currentlyTyping,
  range,
  schema,
}: GenerateAutocompleteSuggestionsProps) => {
  if (!schema || Array.isArray(schema)) return [];

  // Given a {foo: {bar: "baz"}} object typing foo.bar.fo should produce the autocomplete items for foo.bar
  const everythingTypedBeforeTheCurrentWord = currentlyTyping.slice(0, currentlyTyping.length - currentWord.length);
  const lookupPath = getLookupPath(everythingTypedBeforeTheCurrentWord);

  // If the last character typed before the currently unfinished word (if there's one) is a period then we need to look
  // at child objects of autocompleteContext at this level of the tree
  const isObjectLookup = everythingTypedBeforeTheCurrentWord.endsWith(".");
  if (isObjectLookup) {
    const valueForPath = getSchemaDefinitionForDataPath(schema, lookupPath);
    if (!valueForPath) {
      return [];
    }

    // Arrays are meant to be accessed with [, not .
    const isLookingUpArray = valueForPath.type === "array";
    if (isLookingUpArray) {
      return [
        {
          label: 'Current value is an array, try using "[" instead',
          // kind: languages.CompletionItemKind.Field,
          kind: 3,
          insertText: "",
          range,
        },
      ];
    }

    const autocompleteEntries = getSchemaAutocompleteEntries(schema, lookupPath);
    const suggestions = Object.entries(autocompleteEntries).map(([entryPath, entryType]) => {
      const path = `${sanitizePath(lookupPath)}.${entryPath}`;
      return toMonacoAutocompleteItem({
        currentWord,
        insertText: entryPath,
        path,
        range,
        type: entryType,
      });
    });

    return suggestions;
  }

  const isArrayLookup = currentlyTyping.endsWith("[");
  if (isArrayLookup) {
    const valueForPath = getSchemaDefinitionForDataPath(schema, lookupPath);
    if (!valueForPath) {
      return [];
    }

    // Objects are meant to be accessed with ., not [
    const isLookingUpObject = valueForPath.type === "object";
    if (isLookingUpObject) {
      return [];
    }

    const autocompleteEntries = getSchemaAutocompleteEntries(schema, lookupPath);
    const suggestions = Object.entries(autocompleteEntries).map(([entryPath, entryType]) => {
      /**
       * [0] => 0]
       * User already wrote [
       */
      const entryPathWithoutFirstBracket = entryPath.slice(1);
      const path = `${sanitizePath(lookupPath)}[0]`;
      return toMonacoAutocompleteItem({
        currentWord,
        insertText: entryPathWithoutFirstBracket,
        path,
        range,
        type: entryType,
      });
    });

    return suggestions;
  }

  const isAtRootLevel = splitPathBySymbolUnlessWithinQuotesOrBackticks(currentlyTyping, ".").length === 1;
  if (isAtRootLevel) {
    const autocompleteEntries = getSchemaAutocompleteEntries(schema);
    const suggestions = Object.entries(autocompleteEntries).map(([entryPath, entryType]) => {
      return toMonacoAutocompleteItem({
        currentWord,
        insertText: entryPath,
        path: entryPath,
        range,
        type: entryType,
      });
    });

    return suggestions;
  }

  return [];
};

interface GetSortTextIndexForSuggestionProps {
  currentWord: string;
  suggestion: string;
}

type SortTextIndex = undefined | "0_full_match" | "1_ends_with" | "2_starts_with" | "3_partial_match" | "4_fuzzy_match";

interface GetSortTextIndexForSuggestionResult {
  sortText: SortTextIndex;
  matchedPath: string | undefined;
}

/**
 * This function is supposed to help Monaco to sort autocomplete suggestions based on
 * the relevance to the word currently typed by the user.
 *
 * We are trying to bias towards suggestions that are matched more explicitly compared to others,
 * and especially those, matched closer to the end of the path
 */
export const getSortTextIndexForSuggestion = ({
  currentWord,
  suggestion,
}: GetSortTextIndexForSuggestionProps): GetSortTextIndexForSuggestionResult => {
  if (!currentWord) {
    return { sortText: undefined, matchedPath: undefined };
  }

  if (suggestion === currentWord) {
    return { sortText: "0_full_match", matchedPath: undefined };
  }

  if (suggestion.endsWith(currentWord)) {
    return { sortText: "1_ends_with", matchedPath: currentWord };
  }

  if (suggestion.startsWith(currentWord)) {
    return { sortText: "2_starts_with", matchedPath: currentWord };
  }

  if (suggestion.includes(currentWord)) {
    return { sortText: "3_partial_match", matchedPath: currentWord };
  }

  return { sortText: "4_fuzzy_match", matchedPath: undefined };
};

/**
 * Gets the normalized text that user is currently typing in for their active JSONata expression
 */
export const removeJSONataReferences = (lastExpression: string) => {
  let lastExpressionWithoutJSONataReferences = lastExpression;

  const isExplicitRootReference = lastExpressionWithoutJSONataReferences.startsWith("$$.");
  if (isExplicitRootReference) {
    lastExpressionWithoutJSONataReferences = lastExpressionWithoutJSONataReferences.slice(3);
  }

  const isExplicitContextReference = lastExpressionWithoutJSONataReferences.startsWith("$.");
  if (isExplicitContextReference) {
    lastExpressionWithoutJSONataReferences = lastExpressionWithoutJSONataReferences.slice(2);
  }

  /**
   * Used only whenever we operate on an JSON array structure.
   * The `[0].foo.bar` is not a valid JSONata path expression.
   * To make the above expression valid, one needs to append `$` and the start of the expression.
   *
   * Please note that path expressions like `$$.$[0].foo.bar` and `$.$[0].foo.bar` are also valid.
   */
  const isNormalizedArrayRootReference = /^\$\[\d+\]\./.test(lastExpressionWithoutJSONataReferences);
  if (isNormalizedArrayRootReference) {
    lastExpressionWithoutJSONataReferences = lastExpressionWithoutJSONataReferences.slice(1);
  }

  return lastExpressionWithoutJSONataReferences;
};

interface GetAutocompleteContextProps {
  rootJSONSchema: MappingsJSONSchema;
  scopedJSONSchema: ScopedJSONSchema;
  lastExpression: string;
}

/**
 * Decides which schema to use for the autocomplete;
 */
const getAutocompleteJSONSchema = ({
  lastExpression,
  rootJSONSchema,
  scopedJSONSchema,
}: GetAutocompleteContextProps) => {
  if (!scopedJSONSchema) {
    return rootJSONSchema;
  }

  const firstThreeCharacters = lastExpression.slice(0, 3);
  const isRootReference = firstThreeCharacters === "$$.";
  if (isRootReference) {
    return rootJSONSchema;
  }

  return scopedJSONSchema;
};

const getLookupPath = (currentlyTyping: string): string => {
  const isTypingAFunction = currentlyTyping.includes("(");
  const currentPathWithoutLeadingCharacter = currentlyTyping.slice(0, -1);

  if (!isTypingAFunction) {
    return currentPathWithoutLeadingCharacter;
  }

  const currentTextSplitByParentheses = currentlyTyping.split("(");
  // Second to last item contains the last function typed $uppercase($lowercase(foo.)) => $lowercase
  const lastFunctionInCurrentText = currentTextSplitByParentheses[currentTextSplitByParentheses.length - 2];
  const isInsideOfJSONataFunction = builtInFunctionNames.includes(lastFunctionInCurrentText);

  if (!isInsideOfJSONataFunction) {
    return currentPathWithoutLeadingCharacter;
  }

  // Autocomplete inside of a JSONata function
  // e.g. currentlyTyping === "$uppercase(foo."
  // "$uppercase(foo." => "foo"
  const normalizedPathInsideOfFunction = currentTextSplitByParentheses[currentTextSplitByParentheses.length - 1].slice(
    0,
    -1,
  );
  return normalizedPathInsideOfFunction;
};

interface ToMonacoAutocompleteItemProps {
  path: string;
  insertText: string;
  range: IRange;
  currentWord: string;
  type: string;
}

const toMonacoAutocompleteItem = ({
  currentWord,
  insertText,
  path,
  range,
  type,
}: ToMonacoAutocompleteItemProps): languages.CompletionItem => {
  const { matchedPath, sortText } = getSortTextIndexForSuggestion({
    suggestion: path,
    currentWord,
  });

  const label = memoizedGetCompletionLabel(path, currentWord);
  return {
    label,
    // prepending with the matchedPath to enforce better sorting after Monaco's fuzzy matcher
    filterText: matchedPath ? `${matchedPath} ${path}` : path,
    kind: getCompletionItemKind(type),
    detail: capitalCase(type),
    documentation: {
      // Adding 4 spaces to force markdown syntax highlighting
      value: `    ${path}`,
    },
    insertText,
    range,
    sortText,
  };
};

const getCompletionLabel = (path: string, currentWord: string): string => {
  const normalizedCurrentWord = currentWord.toLocaleLowerCase();

  const pathPartsByDot = splitPathBySymbolUnlessWithinQuotesOrBackticks(path, ".");
  const pathPartContainingCurrentWordIndex = pathPartsByDot.findIndex((pathPart) => {
    const normalizedPathPart = pathPart.toLocaleLowerCase();
    const isCurrentWordContainedWithinPathPart = normalizedPathPart.includes(normalizedCurrentWord);

    return isCurrentWordContainedWithinPathPart;
  });

  if (pathPartContainingCurrentWordIndex <= 0) {
    return path;
  }

  const label = pathPartsByDot.slice(pathPartContainingCurrentWordIndex).join(".");
  return `...${label}`;
};
const memoizedGetCompletionLabel = memoize(getCompletionLabel, (path, currentWord) => `${path}${currentWord}`);

const getCompletionItemKind = (completionItemType: string) => {
  if (completionItemType === "object") {
    // return languages.CompletionItemKind.Class;
    return 5;
  }

  // return languages.CompletionItemKind.Field;
  return 3;
};
